/**
 * FridgeIQ — Admin Portal
 * Machines → Transactions → Inventory → Reports → Product Library
 */

import { useState } from "react";
import { useSessions } from "./hooks/useSessions";
import { useMachines } from "./hooks/useMachines";
import { saveMachine } from "./services/dynamodb";
import TransactionLog from "./components/TransactionLog";
import Inventory from "./components/Inventory";
import ProductLibrary from "./components/ProductLibrary";
import Login from "./components/Login";
import { useAuth } from "./hooks/useAuth";

const MACHINE_IDS = (process.env.REACT_APP_MACHINE_IDS || "FIQ-001,FIQ-002").split(",");

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

// ── Edit Machine Modal ────────────────────────────────────────────────────────
function EditMachineModal({ machine, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:    machine.name    || "",
    address: machine.address || "",
    notes:   machine.notes   || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Machine name is required"); return; }
    setSaving(true);
    const ok = await saveMachine({ ...machine, ...form });
    setSaving(false);
    if (ok) onSaved();
    else    setError("Failed to save — check console");
  };

  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8,
    padding: "10px 14px", color: "#f9fafb", fontSize: 13, outline: "none",
  };
  const labelStyle = {
    fontSize: 11, color: "#9ca3af", display: "block",
    marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 20,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#0f1612", border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 16, padding: 28, width: 420,
      }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#f9fafb", marginBottom: 2 }}>
            Edit Machine
          </div>
          <div style={{ fontSize: 12, color: "#4b5563", fontFamily: "monospace" }}>
            {machine.machine_id}
          </div>
        </div>

        {/* Fields */}
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Machine Name *</label>
          <input
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Southern Nutrition — Lobby"
            autoFocus
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Address</label>
          <input
            value={form.address}
            onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
            placeholder="e.g. 123 Main St, Enterprise AL 36330"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Notes</label>
          <input
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="e.g. Near front entrance, left of elevator"
            style={inputStyle}
          />
        </div>

        {error && (
          <div style={{ color: "#f87171", fontSize: 12, marginBottom: 12 }}>⚠️ {error}</div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#9ca3af", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 600,
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 2, background: "rgba(16,185,129,0.2)",
            border: "1px solid rgba(16,185,129,0.4)",
            color: "#10b981", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700,
          }}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Machine Card ──────────────────────────────────────────────────────────────
function StatusDot({ online }) {
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
      background: online ? "#22c55e" : "#ef4444",
      boxShadow: online ? "0 0 6px #22c55e" : "none", flexShrink: 0,
    }} />
  );
}

function MachineCard({ machineId, selected, onSelect, meta, onEdit }) {
  const { sessions, stats, loading } = useSessions(machineId);
  const abnormal   = sessions.filter(s => s.status === "abnormal").length;
  const isSelected = selected === machineId;

  const name    = meta?.name    || machineId;
  const address = meta?.address || "";

  return (
    <div style={{
      background: isSelected ? "rgba(16,185,129,0.06)" : "rgba(255,255,255,0.02)",
      border: `${isSelected ? "1.5px" : "1px"} solid ${isSelected ? "rgba(16,185,129,0.5)" : "rgba(255,255,255,0.08)"}`,
      borderRadius: 16, padding: 20, cursor: "pointer",
      transition: "all 0.2s", position: "relative", overflow: "hidden",
    }}>
      {isSelected && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, transparent, #10b981, transparent)",
        }} />
      )}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ flex: 1 }} onClick={() => onSelect(machineId)}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <StatusDot online={true} />
            <span style={{ fontSize: 11, color: "#22c55e", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Online
            </span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#f9fafb", marginBottom: 2 }}>{name}</div>
          {address && <div style={{ fontSize: 12, color: "#6b7280" }}>{address}</div>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 10 }}>
          {/* Edit button */}
          <button
            onClick={e => { e.stopPropagation(); onEdit(); }}
            style={{
              background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 7, width: 28, height: 28,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#6b7280", fontSize: 13, cursor: "pointer",
              transition: "all 0.15s",
            }}
            title="Edit machine"
          >✏️</button>

          {/* Machine ID badge */}
          <div style={{
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 6, padding: "3px 10px", fontSize: 11, color: "#9ca3af",
            fontFamily: "monospace",
          }}>{machineId}</div>
        </div>
      </div>

      {/* Stats */}
      <div onClick={() => onSelect(machineId)}>
        {loading ? (
          <div style={{ fontSize: 12, color: "#4b5563" }}>Loading...</div>
        ) : stats && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
            {[
              { label: "Today",        value: fmt(stats.revenue),   accent: "#10b981" },
              { label: "Transactions", value: stats.transactions,   accent: "#f9fafb" },
              { label: "Avg Ticket",   value: fmt(stats.avgTicket), accent: "#60a5fa" },
            ].map((s, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "8px 12px" }}>
                <div style={{ fontSize: 10, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>
                  {s.label}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: s.accent }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {abnormal > 0 ? (
            <div style={{
              background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.25)",
              borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#f87171", fontWeight: 600,
            }}>⚠️ {abnormal} abnormal</div>
          ) : <div />}
          <div style={{
            background: isSelected ? "rgba(16,185,129,0.2)" : "rgba(255,255,255,0.06)",
            border: `1px solid ${isSelected ? "rgba(16,185,129,0.4)" : "rgba(255,255,255,0.1)"}`,
            borderRadius: 8, padding: "4px 12px", fontSize: 12,
            color: isSelected ? "#10b981" : "#9ca3af", fontWeight: 600,
          }}>
            {isSelected ? "Selected ✓" : "View →"}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Machine Detail ────────────────────────────────────────────────────────────
function MachineDetail({ machineId, meta }) {
  const [activeTab, setTab] = useState("transactions");
  const { sessions, loading, refresh } = useSessions(machineId);
  const abnormal = sessions.filter(s => s.status === "abnormal").length;

  const tabs = [
    { id: "transactions", label: "Transactions" },
    { id: "inventory",    label: "Inventory" },
    { id: "reports",      label: "Reports" },
  ];

  return (
    <div style={{
      background: "rgba(255,255,255,0.02)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16, overflow: "hidden",
    }}>
      <div style={{
        padding: "18px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(16,185,129,0.04)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 12, color: "#10b981", fontWeight: 600, marginBottom: 2 }}>
            {machineId}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#f9fafb" }}>
            {meta?.name || machineId}
          </div>
          {meta?.address && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{meta.address}</div>
          )}
          {meta?.notes && (
            <div style={{ fontSize: 11, color: "#4b5563", marginTop: 2 }}>📍 {meta.notes}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={refresh} style={{
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
            color: "#9ca3af", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600,
          }}>↻ Refresh</button>
          <button style={{
            background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.35)",
            color: "#10b981", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600,
          }}>🔓 Open Door</button>
        </div>
      </div>

      <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "0 24px" }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setTab(tab.id)} style={{
            background: "none", border: "none",
            borderBottom: activeTab === tab.id ? "2px solid #10b981" : "2px solid transparent",
            padding: "13px 16px", fontSize: 13,
            fontWeight: activeTab === tab.id ? 600 : 400,
            color: activeTab === tab.id ? "#10b981" : "#6b7280",
            cursor: "pointer", marginBottom: -1, transition: "all 0.15s",
          }}>
            {tab.label}
            {tab.id === "transactions" && abnormal > 0 && (
              <span style={{
                marginLeft: 6, background: "#ef4444", color: "white",
                borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 700,
              }}>{abnormal}</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ padding: 24 }}>
        {activeTab === "transactions" && <TransactionLog sessions={sessions} loading={loading} onUpdate={refresh} />}
        {activeTab === "inventory"    && <Inventory machineId={machineId} />}
        {activeTab === "reports"      && (
          <div style={{ color: "#4b5563", fontSize: 13 }}>Revenue charts coming soon.</div>
        )}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const { user, loading, signOut } = useAuth();
  const [selectedId, setSelectedId]   = useState(null);
  const [page, setPage]               = useState("machines");
  const [editingMachine, setEditing]  = useState(null);
  const { machines, refresh: refreshMachines } = useMachines();

  if (loading) return (
    <div style={{
      minHeight: "100vh", background: "#080c0a",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#4b5563", fontFamily: "'DM Sans', system-ui, sans-serif",
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🧊</div>
        <div style={{ fontSize: 13 }}>Loading...</div>
      </div>
    </div>
  );

  if (!user) return <Login onAuthenticated={() => window.location.reload()} />;

  return (
    <div style={{ minHeight: "100vh", background: "#080c0a", color: "#f9fafb" }}>

      {/* Nav */}
      <nav style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 32px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(8,12,10,0.95)", backdropFilter: "blur(12px)",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32,
              background: "linear-gradient(135deg, #10b981, #059669)",
              borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
            }}>🧊</div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#f9fafb", letterSpacing: "-0.02em" }}>
                FridgeIQ
              </div>
              <div style={{ fontSize: 10, color: "#4b5563", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Operator Portal
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 4, marginLeft: 16 }}>
            {[
              { id: "machines", label: "🏪 Machines" },
              { id: "products", label: "📦 Products" },
            ].map(item => (
              <button key={item.id} onClick={() => setPage(item.id)} style={{
                background: page === item.id ? "rgba(16,185,129,0.12)" : "transparent",
                border: `1px solid ${page === item.id ? "rgba(16,185,129,0.3)" : "transparent"}`,
                color: page === item.id ? "#10b981" : "#6b7280",
                borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600,
              }}>{item.label}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ fontSize: 12, color: "#4b5563" }}>
            <span style={{ color: "#22c55e" }}>{MACHINE_IDS.length}</span>/{MACHINE_IDS.length} online
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>{user?.signInDetails?.loginId || user?.username}</div>
            <button onClick={signOut} style={{
              background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)",
              color: "#f87171", borderRadius: 8, padding: "5px 12px",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>Sign Out</button>
          </div>
        </div>
      </nav>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 32px" }}>

        {page === "products" && <ProductLibrary />}

        {page === "machines" && (
          <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 20, alignItems: "start" }}>
            {/* Machine list */}
            <div>
              <div style={{ fontSize: 11, color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
                {MACHINE_IDS.length} Machine{MACHINE_IDS.length !== 1 ? "s" : ""}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {MACHINE_IDS.map(id => (
                  <MachineCard
                    key={id}
                    machineId={id}
                    selected={selectedId}
                    onSelect={setSelectedId}
                    meta={machines[id]}
                    onEdit={() => setEditing(machines[id] || { machine_id: id })}
                  />
                ))}
              </div>
            </div>

            {/* Detail panel */}
            <div>
              {selectedId ? (
                <MachineDetail machineId={selectedId} meta={machines[selectedId]} />
              ) : (
                <div style={{
                  background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.07)",
                  borderRadius: 16, padding: 60, textAlign: "center",
                }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>🧊</div>
                  <div style={{ fontSize: 16, color: "#4b5563", marginBottom: 8 }}>
                    Select a machine to view details
                  </div>
                  <div style={{ fontSize: 13, color: "#374151" }}>
                    Click any machine card to see transactions,<br />inventory, and reports
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit machine modal */}
      {editingMachine && (
        <EditMachineModal
          machine={editingMachine}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refreshMachines(); }}
        />
      )}
    </div>
  );
}
